import React, { useState, useEffect } from "react";

// Components
import {
  Box,
  Card,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Pagination,
  Select,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  InputAdornment,
} from "@mui/material";
import { Scrollbar } from "../../components/scrollbar";
import UserItem from "./UserItem";
import FormDialog from "./FormDialog";
import DeleteDialog from "./DeleteDialog";

// Icons
import MagnifyingGlassIcon from "@heroicons/react/24/solid/MagnifyingGlassIcon";

// Services
import UserService from "../../services/UserService";

const Index = ({ isUserAdmin }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [progressItems, setProgressItems] = useState([]);
  const [selectedItems] = useState([]);
  const [updateItem, setUpdateItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [filter, setFilter] = useState({ keyword: "", type: "" });

  useEffect(() => {
    retreiveUsers();
  }, [page, filter]);

  const retreiveUsers = async () => {
    setLoading(true);
    try {
      const res = await UserService.getAll(page, limit, filter);
      setUsers(res.data.data);
      setTotalPages(res.data.total_pages);
    } catch (error) {
      console.error("Failed to fetch users", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setFilter((prev) => ({ ...prev, keyword: e.target.value }));
    setPage(1);
  };

  const handleFilterRole = (e) => {
    setFilter((prev) => ({ ...prev, type: e.target.value }));
    setPage(1);
  };

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const handleDeleteUser = (user) => {
    setDeleteItem(user);
  };

  const handleConfirmDeleteUser = async (user) => {
    setProgressItems((prev) => [...prev, user]);
    try {
      await UserService.delete(user.id);
      refresh();
    } catch (error) {
      console.error("Failed to delete user", error);
    }
  };

  const handleToggleStatus = async (user, attribute, objectAttribute = null) => {
    if (!objectAttribute) objectAttribute = attribute;
    setProgressItems((prev) => [...prev, user]);
    try {
      await UserService.toggleStatus(user.id, attribute);
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, [objectAttribute]: !u[objectAttribute] } : u)));
    } finally {
      setProgressItems((prev) => prev.filter((item) => item.id !== user.id));
    }
  };

  const refresh = () => {
    setPage(1);
    setUsers([]);
    setProgressItems([]);
    setDeleteItem(null);
    setUpdateItem(null);
    retreiveUsers();
  };

  return (
    <>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 5,
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={3}>
            <Stack direction="row" justifyContent="space-between" spacing={4}>
              <Typography variant="h4">Utilisateurs</Typography>
            </Stack>
            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
              <Card sx={{ p: 2, display: "flex", justifyContent: "space-between", width: "100%" }}>
                <OutlinedInput
                  fullWidth
                  placeholder="Recherche..."
                  startAdornment={
                    <InputAdornment position="start">
                      <SvgIcon color="action" fontSize="small">
                        <MagnifyingGlassIcon />
                      </SvgIcon>
                    </InputAdornment>
                  }
                  sx={{ maxWidth: 500 }}
                  value={filter.keyword}
                  onChange={handleSearchChange}
                />
                <FormControl sx={{ width: "200px" }}>
                  <InputLabel>Type</InputLabel>
                  <Select value={filter.type} onChange={handleFilterRole}>
                    <MenuItem value="">Tous</MenuItem>
                    <MenuItem value="TRANSPORTEUR">Transporteur</MenuItem>
                    <MenuItem value="COMMISSIONNAIRE">Commissionnaire</MenuItem>
                  </Select>
                </FormControl>
              </Card>
            </Box>
            <Card>
              <Scrollbar>
                <Box sx={{ minWidth: 800 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Inscription</TableCell>
                        <TableCell>Utilisateur</TableCell>
                        <TableCell>Téléphone</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Société</TableCell>
                        {isUserAdmin && <TableCell></TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user) => (
                        <UserItem
                          key={user.id}
                          user={user}
                          deleteUser={handleDeleteUser}
                          updateUser={setUpdateItem}
                          isUserAdmin={isUserAdmin}
                          inProgress={progressItems.some((item) => item.id === user.id)}
                          selected={selectedItems.some((item) => item.id === user.id)}
                          toggleStatus={handleToggleStatus}
                        />
                      ))}
                    </TableBody>
                  </Table>
                  {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
                      <CircularProgress />
                    </Box>
                  )}
                </Box>
              </Scrollbar>
              {!loading && (
                <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
                  <Pagination count={totalPages} page={page} onChange={handleChangePage} />
                </Box>
              )}
            </Card>
          </Stack>
        </Container>
      </Box>
      {updateItem && <FormDialog item={updateItem} handleClose={() => setUpdateItem(null)} refresh={refresh} />}
      {deleteItem && (
        <DeleteDialog
          item={deleteItem}
          handleClose={() => setDeleteItem(null)}
          confirmDelete={handleConfirmDeleteUser}
        />
      )}
    </>
  );
};

export default Index;
